import { siteApi } from "@api";
import {
  openCSCompensationDialog,
  type CompensationItem,
} from "@components/features/CSCompensation/CSCompensationDialog/utils";
import { toast } from "@components/utils/Toaster";
import { SEON_FEATURE } from "@configs/seon";
import { useWSCsCompensationUpdated } from "@hooks/useAppWS";
import { useSeonSession } from "@hooks/useSeonSession";
import { resolveAuthResponseError } from "@utils/error-resolvers";
import {
  APIErrorCodes,
  CompensationStatus,
  ErrorResponse,
  QueryError,
  WalletTxMethod,
  bnOrZero,
  fAmount,
} from "kz-ui-sdk";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import useUserMode from "./useUserMode";

const useCSCompensation = () => {
  const { i18n, t } = useTranslation();
  const { isGuest } = useUserMode();

  // Rewards status (polling like SkinnerBox)
  const { data: rewardsStatusData, refetch: refetchRewardsStatus } = siteApi.useGetRewardsStatusQuery(undefined, {
    refetchOnMountOrArgChange: true,
    skip: isGuest,
  });

  // Derive compensation status from rewards status map
  const compensationStatus = useMemo<CompensationStatus | undefined>(() => {
    const data = rewardsStatusData?.[WalletTxMethod.Compensation] as CompensationStatus | undefined;
    return data;
  }, [rewardsStatusData]);

  // Build dialog items from API transactions
  const items = useMemo<CompensationItem[]>(() => {
    const txs = compensationStatus?.transactions ?? [];
    return txs.map((tx, index) => {
      const id = tx?.id;
      const content = tx?.localisables?.find((item) => item.locale?.code === i18n.language)?.content ?? {
        message: "",
        title: "",
      };

      const { message, title } = content;
      const amount = fAmount(bnOrZero(tx?.amount), {
        abbreviate: true,
      });

      const key = `cs-${id}-${index}`;

      return {
        id,
        message,
        amount,
        title,
        key,
      };
    });
  }, [compensationStatus?.transactions, i18n.language]);

  // Listen to WS updates to show FAB in realtime
  useWSCsCompensationUpdated({
    cb: () => {
      refetchRewardsStatus();
    },
  });

  const shouldShow = useMemo(
    () => !!compensationStatus?.enabled && !!compensationStatus?.transactions?.length,
    [compensationStatus?.enabled, compensationStatus?.transactions?.length],
  );

  const [claimReward] = siteApi.useClaimRewardMutation();

  const { getSession: getSeonSession } = useSeonSession(SEON_FEATURE.PROMOTION);

  const openDialog = useCallback(() => {
    if (!shouldShow) return;
    openCSCompensationDialog({
      onClaim: async (item) => {
        // Call claim API analogous to SkinnerBox; server should aggregate per-item claim
        const seonSession = await getSeonSession();
        const claimRewardRes = await claimReward({
          seonSession,
          query: { type: WalletTxMethod.Compensation },
          compensationTxId: item?.id?.toString(),
        });

        if ("error" in claimRewardRes) {
          const error = claimRewardRes.error as QueryError<ErrorResponse>;
          if (
            [APIErrorCodes.Compensation.AlreadyClaimed, APIErrorCodes.Compensation.NotFound]?.includes(
              error?.data?.error?.message as any,
            )
          ) {
            toast.warning(t("compensation already claimed"));
          } else toast.error(resolveAuthResponseError(error, t));
          throw error;
        }
      },
    });
  }, [shouldShow, claimReward, t, getSeonSession]);

  return {
    shouldShowCSCompensation: shouldShow,
    csCompensationItems: items,
    openCSCompensation: openDialog,
    refetchCSCompensation: refetchRewardsStatus,
  };
};

export default useCSCompensation;
